import {zodResolver} from "@hookform/resolvers/zod";
import {AxiosError} from "axios";
import {Loader2, X} from "lucide-react";
import {toast} from "sonner";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {useForm, Controller, useWatch, type DefaultValues} from "react-hook-form";
import {useTranslation} from "react-i18next";

import {useFormDraft, type ClearFormDraft} from "@/src/modules/core/hooks/use-form-draft";
import {useRestoreFormDraft} from "@/src/modules/core/hooks/use-restore-form-draft";
import {createFormInvalidHandler} from "@/src/modules/core/lib/glitchtip/product-telemetry";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {FakeProgress} from "@/components/ui/fake-progress";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import ImagePicker from "@/components/ui/image-picker";
import {Input} from "@/components/ui/input";
import {Item, ItemContent, ItemTitle} from "@/components/ui/item";
import {Textarea} from "@/components/ui/textarea";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/sch-select";
import {TOAST_CONFIG} from "@/lib/constansts";

import {useCreateFiles} from "@/src/modules/files/hooks";
import type {ShiftEndVehicleConditionCheck} from "@/src/modules/officer/types";

import type {PatrolEndStartContext} from "../hooks/use-patrol-end-start-context";
import {useCompanyVehicle} from "../hooks/use-company-vehicle";
import {useCompanyVehicles} from "../hooks/use-company-vehicles";
import {usePatrolEndStartContext} from "../hooks/use-patrol-end-start-context";
import {companyVehiclesService} from "../services";
import {useCreateVehicleConditionCheck} from "../hooks/use-create-vehicle-condition-check";
import {buildShiftEndVehicleConditionCheck} from "../lib/build-shift-end-vehicle-condition-check";
import {formatCompanyVehicleOptionLabel} from "../lib/format-company-vehicle-option-label";
import {formatVehicleFuelLevelLabel} from "../lib/format-vehicle-fuel-level";
import {
  deserializePatrolConditionFormDraftValues,
  getPatrolConditionFormDraftKey,
  getPatrolConditionSheetId,
  patrolConditionHasPersistableContent,
  serializePatrolConditionFormDraftValues,
} from "../lib/patrol-condition-form-draft";
import {clearPatrolStartSnapshot, savePatrolStartSnapshot} from "../lib/patrol-start-snapshot";
import {
  type VehicleServiceRequestOption,
  uniqueVehicleServiceRequests,
  VEHICLE_SERVICE_REQUEST_OPTIONS,
} from "../lib/vehicle-service-requests";
import {
  type VehicleConditionCheckFormValues,
  vehicleConditionCheckFormSchema,
} from "../schemas";
import {type CompanyVehicle, type VehicleConditionCheckPayload, type VehicleFuelLevel} from "../types";

type VehicleOption = {
  id: number;
  label: string;
  raw: CompanyVehicle;
};

type PatrolVehicleConditionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "start" | "end";
  /** Active schedule `shift_id`: POST `shift_id`, list filter, and sessionStorage key. */
  shiftId: number;
  onComplete: () => void | Promise<void>;
  /**
   * When `mode` is `end`, runs after form submit with the nested vehicle check payload.
   * Parent resolves GPS and calls `POST shift/end` atomically (patrol clock-out).
   * Omit for optional flows (e.g. swap vehicle) where the user may cancel.
   */
  onRequestEndShift?: (vehicleConditionCheck: ShiftEndVehicleConditionCheck) => Promise<void>;
  /** Current shift custody vehicle from `GET /shift(s)`; used as end-phase source-of-truth. */
  lockedVehicle?: {
    id: number;
    name?: string | null;
  } | null;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
};

function buildVehicleOptions(vehicles: CompanyVehicle[]): VehicleOption[] {
  return vehicles.map((v) => ({
    id: v.id,
    label: formatCompanyVehicleOptionLabel(v),
    raw: v,
  }));
}

type PatrolFormFieldsProps = {
  mode: "start" | "end";
  shiftId: number;
  startContext: PatrolEndStartContext | null;
  vehicleOptions: VehicleOption[];
  isLoadingVehicles: boolean;
  isSubmitting: boolean;
  createCheck: (
    p: VehicleConditionCheckPayload,
    opts?: {skipEagerInvalidation?: boolean},
  ) => Promise<unknown>;
  onRefreshVehicles?: () => Promise<unknown>;
  onComplete: () => void | Promise<void>;
  onRequestEndShift?: (vehicleConditionCheck: ShiftEndVehicleConditionCheck) => Promise<void>;
  /** End mode: show Cancel (e.g. swap). Omit when `onRequestEndShift` is set (forced complete). */
  onEndCancel?: () => void;
  open: boolean;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
  onDraftInit?: (helpers: {clearDraft: ClearFormDraft}) => void;
};

function PatrolFormFields({
  mode,
  shiftId,
  startContext,
  vehicleOptions,
  isLoadingVehicles,
  isSubmitting,
  createCheck,
  onRefreshVehicles,
  onComplete,
  onRequestEndShift,
  onEndCancel,
  open,
  draftRestoreValues,
  onDraftRestored,
  onDraftInit,
}: PatrolFormFieldsProps) {
  const {t} = useTranslation("company_vehicle");
  const {t: tCommon} = useTranslation("common");
  const {mutateAsync: mutateCreateFiles} = useCreateFiles();
  const skipOpenResetRef = useRef(false);
  const restoredFromDraftRef = useRef(false);
  /** After the officer edits mileage, never overwrite from async vehicle/detail loads. */
  const odometerTouchedRef = useRef(false);

  const schema = vehicleConditionCheckFormSchema;

  const defaultValues: DefaultValues<VehicleConditionCheckFormValues> = useMemo(
    () =>
      mode === "end" && startContext
        ? {
            company_vehicle_id: startContext.company_vehicle_id,
            odometer_miles:
              startContext.end_mileage_floor_miles != null &&
              Number.isFinite(Number(startContext.end_mileage_floor_miles))
                ? Number(startContext.end_mileage_floor_miles)
                : undefined,
            fuel_level: "",
            service_requests: [],
            notes: "",
            files: [],
          }
        : {
            company_vehicle_id: undefined,
            odometer_miles: undefined,
            fuel_level: "",
            service_requests: [],
            notes: "",
            files: [],
          },
    [mode, startContext],
  );

  const form = useForm<VehicleConditionCheckFormValues>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const {getValues, watch, reset} = form;

  const draftKey = getPatrolConditionFormDraftKey(mode, shiftId);
  const sheetId = getPatrolConditionSheetId(mode);

  const {snapshotDraft, clearDraft} = useFormDraft({
    draftKey,
    moduleLabel: "Vehicle condition check",
    route: "/home",
    sheetId,
    entityId: shiftId,
    enabled: open,
    getValues,
    serializeValues: serializePatrolConditionFormDraftValues,
    shouldPersist: () => patrolConditionHasPersistableContent(getValues()),
    watch,
    abandonTelemetry: {
      feature: "companyvehicles",
      operation:
        mode === "start" ? "patrol_vehicle_condition_start" : "patrol_vehicle_condition_end",
    },
  });

  useEffect(() => {
    onDraftInit?.({clearDraft});
  }, [clearDraft, onDraftInit]);

  useEffect(() => {
    if (!open) {
      skipOpenResetRef.current = false;
      restoredFromDraftRef.current = false;
      odometerTouchedRef.current = false;
      return;
    }
    if (skipOpenResetRef.current || draftRestoreValues || restoredFromDraftRef.current) {
      return;
    }
    reset(defaultValues);
    skipOpenResetRef.current = true;
  }, [defaultValues, draftRestoreValues, open, reset]);

  useRestoreFormDraft({
    draftRestoreValues,
    onRestore: () => {
      reset({
        ...defaultValues,
        ...(deserializePatrolConditionFormDraftValues(
          draftRestoreValues!,
        ) as VehicleConditionCheckFormValues),
      });
      skipOpenResetRef.current = true;
      restoredFromDraftRef.current = true;
      onDraftRestored?.();
    },
  });

  const selectedVehicleId = useWatch({control: form.control, name: "company_vehicle_id"});
  const filesWatch = useWatch({control: form.control, name: "files", defaultValue: []});
  const selectedVehicle = useMemo(
    () => vehicleOptions.find((o) => o.id === selectedVehicleId),
    [vehicleOptions, selectedVehicleId],
  );
  const listMileage = selectedVehicle?.raw.current_mileage;
  const listMileageOk = listMileage != null && Number.isFinite(Number(listMileage));
  const canShowCompanyVehicle = companyVehiclesService.getCanShowCompanyVehicle();
  const shouldLoadOdometerFromDetail =
    mode === "start" &&
    canShowCompanyVehicle &&
    selectedVehicleId != null &&
    !listMileageOk;

  const {vehicle: selectedVehicleDetail} = useCompanyVehicle(
    mode === "start" && selectedVehicleId != null ? selectedVehicleId : null,
    {enabled: shouldLoadOdometerFromDetail},
  );

  useEffect(() => {
    if (mode !== "start" || !selectedVehicleDetail || odometerTouchedRef.current) return;
    if (form.getValues("company_vehicle_id") !== selectedVehicleDetail.id) return;
    const current = form.getValues("odometer_miles");
    if (current != null && Number.isFinite(Number(current))) return;
    const cm = selectedVehicleDetail.current_mileage;
    if (cm == null || !Number.isFinite(Number(cm))) return;
    form.setValue("odometer_miles", Number(cm));
  }, [form, mode, selectedVehicleDetail]);

  const onSubmit = async (values: VehicleConditionCheckFormValues) => {
    const rawFiles = values.files ?? [];
    let fileUrls = rawFiles.filter((f): f is string => !(f instanceof File));
    const filesToUpload = rawFiles.filter((f): f is File => f instanceof File);

    if (filesToUpload.length > 0 && !navigator.onLine) {
      toast.error(
        t("toast.upload_offline"),
        TOAST_CONFIG,
      );
      return;
    }

    if (filesToUpload.length > 0) {
      try {
        const uploaded = await mutateCreateFiles({
          files: filesToUpload,
          upload_context: "vehicle_condition_check",
        });
        fileUrls = [...fileUrls, ...uploaded];
      } catch {
        return;
      }
    }

    if (mode === "end" && onRequestEndShift) {
      const vehicleConditionCheck = buildShiftEndVehicleConditionCheck(values, fileUrls);
      await onRequestEndShift(vehicleConditionCheck);
      clearPatrolStartSnapshot(shiftId);
      clearDraft({submitted: true});
      await onComplete();
      return;
    }

    const mileage =
      values.odometer_miles != null && Number.isFinite(Number(values.odometer_miles))
        ? Number(values.odometer_miles)
        : 0;
    const payload: VehicleConditionCheckPayload = {
      shift_id: shiftId,
      company_vehicle_id: values.company_vehicle_id,
      phase: mode === "start" ? "start" : "end",
      mileage,
      fuel_level: values.fuel_level as VehicleFuelLevel,
      service_requests:
        mode === "start"
          ? []
          : uniqueVehicleServiceRequests(values.service_requests ?? []),
      notes: values.notes?.trim() || null,
      ...(fileUrls.length > 0 ? {files: fileUrls} : {}),
    };
    try {
      await createCheck(payload, {
        skipEagerInvalidation: false,
      });
    } catch (error) {
      const axiosError = error as AxiosError<{
        message?: string;
        error_code?: string;
      }>;
      const code = axiosError.response?.data?.error_code;
      const message = axiosError.response?.data?.message ?? "";
      const isAlreadyInUse =
        code === "already_in_use" ||
        message.toLowerCase().includes("already in use by another active shift");
      if (mode === "start" && isAlreadyInUse && onRefreshVehicles) {
        await onRefreshVehicles();
      }
      return;
    }

    if (mode === "start" && values.company_vehicle_id) {
      const opt = buildVehicleOptions(vehicleOptions.map((o) => o.raw)).find(
        (o) => o.id === values.company_vehicle_id,
      );
      const miles = values.odometer_miles;
      const odometer_miles =
        miles != null && Number.isFinite(Number(miles)) ? Number(miles) : null;
      savePatrolStartSnapshot(shiftId, {
        company_vehicle_id: values.company_vehicle_id,
        odometer_miles,
        vehicle_label: opt?.label ?? `Vehicle #${values.company_vehicle_id}`,
      });
    }
    if (mode === "end") {
      clearPatrolStartSnapshot(shiftId);
    }
    clearDraft({submitted: true});
    await onComplete();
  };

  if (isLoadingVehicles && mode === "start") {
    return <p className="text-muted-foreground text-sm">{t("loading")}</p>;
  }
  if (mode === "start" && vehicleOptions.length === 0) {
    return (
      <p className="text-destructive text-sm">
        No company vehicles are available. Contact a supervisor to assign a vehicle to your company.
      </p>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          onSubmit,
          createFormInvalidHandler({
            feature: "companyvehicles",
            operation:
              mode === "start" ? "patrol_vehicle_condition_start" : "patrol_vehicle_condition_end",
            sheetId,
          }),
        )}
        className="flex flex-col gap-4"
      >
        {mode === "end" && startContext ? (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <p className="text-muted-foreground text-xs font-medium">Company vehicle (locked)</p>
            <p className="font-medium leading-snug">{startContext.vehicle_label}</p>
          </div>
        ) : (
          <Controller
            control={form.control}
            name="company_vehicle_id"
            render={({field, fieldState}) => {
              const value = vehicleOptions.find((o) => o.id === field.value) ?? null;
              return (
                <FormItem>
                  <FormLabel required>Company vehicle</FormLabel>
                  <FormControl>
                    <ComboboxGeneric<VehicleOption>
                      items={vehicleOptions}
                      value={value}
                      onChange={(item) => {
                        field.onChange(item?.id ?? undefined);
                        if (mode !== "start") return;
                        if (!item) {
                          form.setValue("odometer_miles", undefined);
                          return;
                        }
                        const m = item.raw.current_mileage;
                        if (m != null && Number.isFinite(Number(m))) {
                          form.setValue("odometer_miles", Number(m));
                        } else {
                          form.setValue("odometer_miles", undefined);
                        }
                      }}
                      getItemKey={(o) => o.id}
                      getItemLabel={(o) => o.label}
                      isItemEqual={(a, b) => a.id === b?.id}
                      placeholder={tCommon("select_placeholder", {entity: tCommon("vehicle")})}
                      emptyMessage="No vehicles found."
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  {fieldState.error ? <FormMessage /> : null}
                </FormItem>
              );
            }}
          />
        )}

        <FormField
          control={form.control}
          name="odometer_miles"
          render={({field}) => (
            <FormItem>
              <FormLabel>Mileage (odometer)</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  disabled={isSubmitting}
                  value={field.value == null ? "" : String(field.value)}
                  onFocus={() => {
                    odometerTouchedRef.current = true;
                  }}
                  onChange={(e) => {
                    odometerTouchedRef.current = true;
                    const raw = e.target.value;
                    if (raw === "") {
                      field.onChange(undefined);
                      return;
                    }
                    const digits = raw.replace(/\D/g, "");
                    if (digits === "") {
                      field.onChange(undefined);
                      return;
                    }
                    field.onChange(Number(digits));
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="fuel_level"
          render={({field}) => (
            <FormItem>
              <FormLabel required>Fuel Level</FormLabel>
              <FormControl>
                <Select
                  value={field.value ?? ""}
                  onValueChange={(v) => field.onChange(v as VehicleFuelLevel)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("form.select_fuel_level_placeholder")}
                    >
                      {formatVehicleFuelLevelLabel(field.value) || null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="" disabled>
                      {t("form.select_fuel_level_placeholder")}
                    </SelectItem>
                    <SelectItem value="empty">Empty</SelectItem>
                    <SelectItem value="quarter">1/4</SelectItem>
                    <SelectItem value="half">1/2</SelectItem>
                    <SelectItem value="three_quarters">3/4</SelectItem>
                    <SelectItem value="full">Full</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {mode === "end" ? (
          <FormField
            control={form.control}
            name="service_requests"
            render={({field}) => {
              const selectedIds = uniqueVehicleServiceRequests(field.value ?? []);
              const value: VehicleServiceRequestOption[] = VEHICLE_SERVICE_REQUEST_OPTIONS.filter(
                (o) => selectedIds.includes(o.id),
              );
              const allSelected = selectedIds.length >= VEHICLE_SERVICE_REQUEST_OPTIONS.length;
              return (
                <FormItem>
                  <div className="flex items-center justify-between gap-2">
                    <FormLabel>Service requests</FormLabel>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isSubmitting || allSelected}
                      onClick={() => {
                        field.onChange(VEHICLE_SERVICE_REQUEST_OPTIONS.map((o) => o.id));
                      }}
                    >
                      Add all
                    </Button>
                  </div>
                  <p className="text-muted-foreground mt-0 mb-2 text-xs">
                    Optional. Select any maintenance or follow-up you need.
                  </p>
                  <FormControl>
                    <ComboboxGeneric<VehicleServiceRequestOption>
                      multiple
                      shouldRenderSelection={false}
                      items={VEHICLE_SERVICE_REQUEST_OPTIONS}
                      value={value}
                      onChange={(next) => {
                        field.onChange(
                          uniqueVehicleServiceRequests((next ?? []).map((o) => o.id)),
                        );
                      }}
                      getItemKey={(o) => o.id}
                      getItemLabel={(o) => o.label}
                      isItemEqual={(a, b) => a.id === b?.id}
                      placeholder={t("form.select_service_requests_placeholder")}
                      emptyMessage="No matching service requests."
                      disabled={isSubmitting}
                      className="w-full"
                      renderItem={(o) => (
                        <Item size="sm" className="p-0">
                          <ItemContent>
                            <ItemTitle>{o.label}</ItemTitle>
                          </ItemContent>
                        </Item>
                      )}
                    />
                  </FormControl>
                  {selectedIds.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {value.map((o) => (
                        <Badge key={`service-request-${o.id}`} variant="secondary">
                          {o.label}
                          <button
                            type="button"
                            className="ring-offset-background focus:ring-ring ml-1 rounded-full outline-none focus:ring-2 focus:ring-offset-2"
                            disabled={isSubmitting}
                            onClick={() => {
                              field.onChange(
                                uniqueVehicleServiceRequests(
                                  selectedIds.filter((id) => id !== o.id),
                                ),
                              );
                            }}
                          >
                            <X className="text-muted-foreground hover:text-foreground size-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        ) : null}

        <FormField
          control={form.control}
          name="files"
          render={({field}) => (
            <FormItem>
              <FormLabel>Photos</FormLabel>
              <FormControl>
                <ImagePicker
                  images={field.value ?? []}
                  errors={undefined}
                  immediateUpload
                  captureFeature="companyvehicles"
                  captureOperation={
                    mode === "start"
                      ? "patrol_vehicle_condition_start"
                      : "patrol_vehicle_condition_end"
                  }
                  onBeforePick={snapshotDraft}
                  onUpload={(next: string[]) => field.onChange(next)}
                  onDelete={(index: number) => {
                    const cur = field.value ?? [];
                    field.onChange(cur.filter((_, i) => i !== index));
                    snapshotDraft();
                  }}
                />
              </FormControl>
              {(filesWatch?.length ?? 0) > 0 && isSubmitting ? <FakeProgress /> : null}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({field}) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea className="min-h-[4rem] resize-y" disabled={isSubmitting} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter className="flex-col gap-2 sm:justify-end">
          {mode === "end" && onEndCancel ? (
            <Button
              type="button"
              variant="ghost"
              disabled={isSubmitting}
              onClick={onEndCancel}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            type="submit"
            className="w-full sm:w-auto"
            disabled={isSubmitting || (mode === "start" && isLoadingVehicles)}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Saving…
              </>
            ) : mode === "start" ? (
              "Start Check"
            ) : onRequestEndShift ? (
              "End shift"
            ) : (
              "End Check"
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function PatrolVehicleConditionDialog({
  open,
  onOpenChange,
  mode,
  shiftId,
  onComplete,
  onRequestEndShift,
  lockedVehicle = null,
  draftRestoreValues,
  onDraftRestored,
}: PatrolVehicleConditionDialogProps) {
  const {t} = useTranslation("common");
  const draftHelpersRef = useRef<{clearDraft: ClearFormDraft} | null>(null);
  const {mutateAsync: createCheckMutate, isPending: isSubmitting} = useCreateVehicleConditionCheck();

  const createCheck = useCallback(
    async (payload: VehicleConditionCheckPayload, opts?: {skipEagerInvalidation?: boolean}) => {
      await createCheckMutate({
        payload,
        skipEagerInvalidation: opts?.skipEagerInvalidation,
      });
    },
    [createCheckMutate],
  );

  const lockedId =
    lockedVehicle && typeof lockedVehicle.id === "number" ? lockedVehicle.id : null;

  const {startContext: hookStartContext, isLoadingStart} = usePatrolEndStartContext(
    shiftId,
    mode,
    open,
  );

  const canShowVehicle = companyVehiclesService.getCanShowCompanyVehicle();
  const {vehicle: lockedVehicleDetail, isPending: isLoadingVehicleDetail} = useCompanyVehicle(
    lockedId,
    {enabled: Boolean(open && mode === "end" && lockedId != null)},
  );

  const startContext = useMemo<PatrolEndStartContext | null>(() => {
    if (mode !== "end" || lockedId == null || !lockedVehicle) return null;

    const label = lockedVehicle.name?.trim() || `Vehicle #${lockedId}`;
    const start_odometer_miles = hookStartContext?.start_odometer_miles ?? null;

    const vehicleMiles =
      lockedVehicleDetail?.current_mileage != null &&
      Number.isFinite(Number(lockedVehicleDetail.current_mileage))
        ? Number(lockedVehicleDetail.current_mileage)
        : null;

    const candidates = [start_odometer_miles, vehicleMiles].filter(
      (n): n is number => n != null && Number.isFinite(n),
    );
    const end_mileage_floor_miles =
      candidates.length === 0 ? null : Math.max(...candidates);

    return {
      company_vehicle_id: lockedId,
      start_odometer_miles,
      vehicle_label: label,
      end_mileage_floor_miles,
    };
  }, [
    mode,
    lockedId,
    lockedVehicle,
    hookStartContext?.start_odometer_miles,
    lockedVehicleDetail?.current_mileage,
  ]);

  const {vehicles, isPending: isLoadingVehicles, refetch: refetchVehicles} = useCompanyVehicles(
    {page: {limit: 200, offset: 0}, filter: {available_for_assignment: true}},
    {enabled: open && mode === "start"},
  );
  const vehicleOptions = useMemo(() => buildVehicleOptions(vehicles), [vehicles]);

  const formRemountKey = `patrol-${mode}-${shiftId}`;

  const showEndForm =
    mode === "end" &&
    Boolean(startContext) &&
    !isLoadingStart &&
    !(canShowVehicle && lockedId != null && isLoadingVehicleDetail);
  const showStartForm = mode === "start";
  const showEndLoading =
    mode === "end" &&
    open &&
    Boolean(lockedVehicle) &&
    (isLoadingStart || (canShowVehicle && lockedId != null && isLoadingVehicleDetail));
  const showEndMissing = mode === "end" && (!lockedVehicle || lockedId == null);

  const blockDismiss = showStartForm && vehicleOptions.length > 0;
  /**
   * Block closing only when ending shift requires this check (`onRequestEndShift` set).
   * Swap / optional flows omit it — user may dismiss (Cancel, X, overlay).
   */
  const blockEndCheckDismiss =
    mode === "end" && !showEndMissing && onRequestEndShift != null;
  const showCloseButton =
    showEndMissing || (!blockDismiss && !blockEndCheckDismiss);
  const disablePointerDismissal = blockDismiss || blockEndCheckDismiss;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (blockDismiss || blockEndCheckDismiss) {
        return;
      }
      draftHelpersRef.current?.clearDraft();
    }
    onOpenChange(next);
  };

  const endCancelHandler = onRequestEndShift ? undefined : () => handleOpenChange(false);

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      disablePointerDismissal={disablePointerDismissal}
    >
      <DialogContent
        showCloseButton={showCloseButton}
        className="max-h-[min(92vh,52rem)] overflow-y-auto sm:max-w-xl"
      >
        <DialogHeader>
          <DialogTitle>Vehicle Condition Check</DialogTitle>
          <DialogDescription>
            {showStartForm
              ? "Log the company vehicle you are using for this patrol shift before you continue."
              : showEndLoading
                ? "Looking up your start-of-shift vehicle check…"
                : showEndMissing
                  ? "Could not identify the currently held vehicle from shift data. Refresh and try again."
                  : "Log the current status for the same vehicle you used at shift start, then you can end your shift."}
          </DialogDescription>
        </DialogHeader>

        {showEndLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 shrink-0 animate-spin" />
            {t("loading")}
          </div>
        ) : null}

        {showEndMissing ? (
          <p className="text-destructive text-sm">
            End checks must target the vehicle currently held by this shift. Refresh shift data and
            try again.
          </p>
        ) : null}

        {showStartForm && !showEndLoading ? (
          <PatrolFormFields
            key={formRemountKey}
            mode="start"
            shiftId={shiftId}
            startContext={null}
            vehicleOptions={vehicleOptions}
            isLoadingVehicles={isLoadingVehicles}
            isSubmitting={isSubmitting}
            createCheck={createCheck}
            onRefreshVehicles={refetchVehicles}
            onComplete={onComplete}
            onRequestEndShift={onRequestEndShift}
            open={open}
            draftRestoreValues={draftRestoreValues}
            onDraftRestored={onDraftRestored}
            onDraftInit={(helpers) => {
              draftHelpersRef.current = helpers;
            }}
          />
        ) : null}

        {showEndForm ? (
          <PatrolFormFields
            key={formRemountKey}
            mode="end"
            shiftId={shiftId}
            startContext={startContext}
            vehicleOptions={[]}
            isLoadingVehicles={false}
            isSubmitting={isSubmitting}
            createCheck={createCheck}
            onRefreshVehicles={undefined}
            onComplete={onComplete}
            onRequestEndShift={onRequestEndShift}
            onEndCancel={endCancelHandler}
            open={open}
            draftRestoreValues={draftRestoreValues}
            onDraftRestored={onDraftRestored}
            onDraftInit={(helpers) => {
              draftHelpersRef.current = helpers;
            }}
          />
        ) : null}

        {showEndMissing ? (
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
